<template>
  <PForm
    ref="pForm"
    :heading="heading"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    @close="handleCloseForm"
    @reset="handleResetForm"
    @submit.prevent="onSubmit"
  >
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div :class="className">
          <PSelectInput
            v-if="isRequestAgainstInvoice"
            v-model="formData.invoice"
            :options="
              unref(invoiceFilter.entities)?.filter((invoice) => {
                return invoice.status_cd !== 'cancelled' && invoice.number;
              })
            "
            input-class="ellipsis"
            label="Invoice"
            option-label="number"
            option-value="_id"
            :emit-value="false"
            :map-options="false"
            placeholder="Select an invoice"
            required
            @virtual-scroll="invoiceFilter.onScroll"
            @filter="invoiceFilter.onFilter"
            @filter-abort="invoiceFilter.onFilterAbort"
            @update:model-value="populateFormData"
          />

          <PSelectInput
            v-if="isRequestAgainstPurchaseBill"
            v-model="formData.bill"
            :disable="ewayBillRequestFormState.view"
            :options="unref(billFilter.entities)"
            input-class="ellipsis"
            label="Purchase Bill"
            option-label="number"
            option-value="_id"
            :emit-value="false"
            :map-options="false"
            placeholder="Select a Purchase Bill"
            required
            @virtual-scroll="billFilter.onScroll"
            @filter="billFilter.onFilter"
            @filter-abort="billFilter.onFilterAbort"
            @update:model-value="populateFormData"
          />
        </div>
        <div v-if="isRequestAgainstPurchaseBill" class="col-6">
          <PInput
            v-model.trim="formData.number"
            required
            label="E-Way Bill No"
            :rules="[handleEWBNumberValidation]"
            :loading="validatingEWBNumber"
            debounce="300"
            :disable="ewayBillRequestFormState.view"
          />
        </div>
        <div :class="className">
          <PVDateInput
            v-if="isRequestAgainstInvoice"
            v-model="formData.invc_date"
            label="Invoice Date"
            placeholder="DD-MM-YYYY"
            :disable="true"
            required
          />
          <PVDateInput
            v-if="isRequestAgainstPurchaseBill"
            v-model="formData.date"
            :min-date="minEWayBillDate"
            label="E-Way Bill Date"
            placeholder="DD-MM-YYYY"
            :disable="!formData.bill_id || ewayBillRequestFormState.view"
            required
          />
        </div>
        <div :class="className">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            disable
            required
          />
        </div>
      </div>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6 column">
          <PSelectInput
            v-if="isRequestAgainstPurchaseBill"
            v-model="formData.dispatch_address"
            :cta="
              ewayBillRequestFormState.view
                ? null
                : addNewAddressButton('dispatch', formData?.seller?._id)
            "
            :disable="!formData?.seller?._id"
            :options="unref(sellerDispatchAddressFilter.entities)"
            :option-label="getAddressOptionLabel"
            label="Dispatch From"
            required
            placeholder="Select an address"
            @virtual-scroll="sellerDispatchAddressFilter.onScroll"
            @filter="sellerDispatchAddressFilter.onFilter"
            @filter-abort="sellerDispatchAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'dispatch_address',
                  null,
                  formData?.seller?.shop_name,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).dispatch_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
              />
            </template>
          </PSelectInput>
          <PInput
            v-if="isRequestAgainstPurchaseBill"
            v-model="formData.dispatch_address"
            class="col"
            placeholder="Enter dispatch address"
            type="textarea"
            autogrow
            disable
            required
          />
          <PInput
            v-if="isRequestAgainstInvoice"
            v-model="formData.dispatch_address"
            class="col"
            label="Dispatch Address"
            placeholder="Enter dispatch address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
        <div class="col-6 column">
          <PSelectInput
            v-if="isRequestAgainstPurchaseBill"
            v-model="temp_shipping_address"
            disable
            label="Shipping Address"
            required
            placeholder="Select Shipping address"
          >
          </PSelectInput>
          <PInput
            v-if="isRequestAgainstPurchaseBill"
            v-model="temp_shipping_address"
            class="col"
            placeholder="Enter shipping address"
            type="textarea"
            autogrow
            disable
            required
          />
          <PInput
            v-if="isRequestAgainstInvoice"
            v-model="temp_shipping_address"
            class="col"
            label="Shipping Address"
            placeholder="Enter shipping address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
      </div>
    </div>
    <q-separator
      v-if="isRequestAgainstPurchaseBill && !ewayBillRequestFormState.view"
      spaced="xl"
    />
    <div
      v-if="isRequestAgainstPurchaseBill && !ewayBillRequestFormState.view"
      class="row q-col-gutter-lg"
    >
      <div class="col-12">
        <PFile
          v-model="file"
          label="Upload Proof Of Document ( E-Way File)"
          help="Allowed file types are PDFs, images, excel, csv"
          required
        />
        <PFile
          v-if="file"
          v-model="file"
          class="q-mt-xxs"
          variant="re-upload"
        />
        <FilesList
          :entity="formData"
          :removal-allowed="false"
          file-type="Purchase Bill"
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { useCheckDuplicateEWBNumber } from "src/composables/checkDuplicateEWBNumber";
import eWayBillRequestFormModel from "src/modules/purchase-order/models/eWayBillRequestFrom";
import { notify } from "assets/scripts/utilities/api-clients";
import PInput from "src/components/PInput.vue";
const pForm = ref(null);
const { ewayBillRequestFormState, eWayBill } = storeToRefs(useEWayBillStore());
const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());
const { toggleAddAddressForm } = useAddressStore();
const { validateEWBNumber, validatingEWBNumber } = useCheckDuplicateEWBNumber();

let invoiceFilter = ref({});
const props = defineProps({
  requestAgainst: {
    type: String,
    default: "invoice",
  },
});

const isRequestAgainstInvoice = computed(
  () => props.requestAgainst === "invoice",
);

const isRequestAgainstPurchaseBill = computed(
  () => props.requestAgainst === "purchase_bill",
);

const className = computed(() => {
  if (unref(isRequestAgainstInvoice)) {
    return "col-4";
  }

  return "col-6";
});

const minEWayBillDate = ref(null);

const billFilter = ref({});
let sellerDispatchAddressFilter = useFilter({
  entities: [],
});

watchEffect(() => {
  if (unref(isRequestAgainstInvoice)) {
    invoiceFilter.value = useFilter({
      entity: "invoice",
      options: {
        params: {
          po_id: unref(purchaseOrder)._id,
          "eWayBill{}": {
            status_cd: "active",
            operation: 0,
          },
        },
      },
    });

    return;
  }

  billFilter.value = useFilter({
    entity: "purchaseBill",
    options: {
      params: {
        status_cd: "active",
        po_id: unref(purchaseOrder)?._id,
        eWayBill: {
          operation: 0,
        },
      },
    },
  });
});

const { getEWayBills, postEWayBill, putEWayBill, toggleEWayBillRequestForm } =
  useEWayBillStore();

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: eWayBillRequestFormModel(),
  entity: eWayBill,
  formRef: pForm,
  isForm: ewayBillRequestFormState,
  post: postEWayBill,
  put: putEWayBill,
  toggleForm: toggleEWayBillRequestForm,
});
const { file, uploadFile } = useFile(formData);
const temp_shipping_address = ref("");
const temp_billing_address = ref("");

const resetShippingAddress = () => {
  temp_shipping_address.value = null;
};

const addNewAddressButton = (addressType, userId) => {
  return {
    dense: true,
    flat: true,
    fluid: true,
    text: true,
    icon: "sym_r_add",
    label: `Add New Address`,
    disabled: !unref(formData).bill_id,
    onClick: () => addNewAddress(addressType, userId),
  };
};
const addNewAddress = (addressType, userId) => {
  if (!userId) {
    return;
  }

  toggleAddAddressForm({
    open: true,
    level: 0,
    props: {
      documentType: "E-Way Bill",
      userId: userId,
      addressType: addressType,
      updateParent: selectAddress,
    },
  });
};
const selectAddress = (newAddressData) => {
  if (!newAddressData) {
    return;
  }

  if (newAddressData.type === "billing") {
    updateAddress(
      newAddressData,
      "billing_address",
      unref(purchaseOrder).buyer?.gstin,
      unref(purchaseOrder).buyer?.shop_name,
    );
  } else if (newAddressData.type === "shipping") {
    updateAddress(
      newAddressData,
      "shipping_address",
      unref(purchaseOrder).buyer?.gstin,
      unref(purchaseOrder).buyer?.shop_name,
    );
  } else if (newAddressData.type === "dispatch") {
    updateAddress(
      newAddressData,
      "dispatch_address",
      null,
      unref(purchaseOrder)?.seller?.shop_name,
    );
  }
};

const updateAddress = (info, address_type, gstin, shop_name) => {
  if (!info) {
    return;
  }

  unref(formData)[`${address_type}_info`] = {
    city: info.city,
    state: info.state,
    pincode: info.pincode,
    address_line: info.address_line,
    state_code: info.state_code,
    shop_name,
    address_id: info?._id,
  };

  const gstinText = gstin ? `\nGSTIN: ${gstin}` : "";
  const concatenatedAddress = `${info.address_line ? info.address_line + ", " : ""}${info.city ? info.city + ", " : ""}${info.state ? info.state + ", " : ""}${info.pincode ? info.pincode + " " : ""}${gstinText}`;

  unref(formData)[`${address_type}`] = concatenatedAddress;

  if (address_type === "billing_address") {
    temp_billing_address.value = `${shop_name}\n${concatenatedAddress}`;
  } else if (address_type === "shipping_address") {
    temp_shipping_address.value = `${shop_name}\n${concatenatedAddress}`;
  }
};
const getAddressOptionLabel = (address, gstin) => {
  if (!address) return;
  else if (typeof address === "string") {
    return address;
  } else if (typeof address === "object") {
    const addressLine = address.address_line || address.address;

    return (
      (addressLine ? addressLine + ", " : "") +
      (address.city ? address.city + ", " : "") +
      (address.state ? address.state + ", " : "") +
      (address.pincode ? address.pincode + " " : "") +
      (gstin ? "\nGSTIN: " + gstin : "")
    );
  }
};

const resetBillRelatedFields = () => {
  minEWayBillDate.value = null;

  formData.value.number = null;

  formData.value.date = null;

  formData.value.dispatch_address = "";
};
const populateBillRelatedData = (bill) => {
  resetBillRelatedFields();

  const {
    _id,
    po_id,
    shipping_address,
    shipping_address_info,
    vehicle_number,
    date,
    seller,
  } = bill;

  sellerDispatchAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "dispatch",
        user_id: seller?._id,
      },
    },
  });

  minEWayBillDate.value = new Date(date);

  formData.value = {
    ...unref(formData),
    bill_id: _id,
    po_id: po_id._id || po_id,
    shipping_address,
    shipping_address_info,
    vehicle_number,
    seller,
  };

  temp_shipping_address.value = `${shipping_address}`;
};

const populateFormData = (entity) => {
  if (unref(isRequestAgainstPurchaseBill)) {
    populateBillRelatedData(entity);

    return;
  }

  const {
    _id,
    date,
    dispatch_address,
    dispatch_address_info,
    number,
    po_id,
    shipping_address,
    shipping_address_info,
    total_amount,
    vehicle_number,
  } = entity;

  formData.value = {
    ...unref(formData),
    invc_id: _id,
    dispatch_address,
    dispatch_address_info,
    invc_date: date || new Date(),
    invc_number: number,
    invc_total_amount: total_amount,
    po_id: po_id._id || po_id,
    shipping_address,
    shipping_address_info,
    vehicle_number,
  };

  temp_shipping_address.value = `${shipping_address_info?.shop_name ?? ""}\n${shipping_address}`;
};

const submitButton = computed(() => {
  if (unref(ewayBillRequestFormState).view) {
    return {};
  }

  return {
    loading: unref(isLoading),
    disable: unref(validatingEWBNumber),
  };
});

const { getCounts, getPurchaseOrderFiles } = usePurchaseOrderStore();

const onSubmit = async () => {
  try {
    const submission = await submitForm(false);

    isLoading.value = true;

    if (unref(file)) {
      await uploadFile({
        ...submission?.data?.data,
        type: "E-Way Bill",
        showMessage: false,
      });
    }

    await Promise.all([getCounts(), getPurchaseOrderFiles(), getEWayBills()]);

    isLoading.value = false;

    closeForm(true);

    submission?.data?.messages?.forEach((info) => {
      notify({
        type: info.type,
        message: info.msg,
      });
    });
  } catch (error) {
    isLoading.value = false;

    if (error?.data?.message) {
      notify({
        type: "error",
        message: error.data.msg,
      });
    }
  }
};

const handleResetForm = () => {
  resetShippingAddress();

  resetForm();
};
const handleCloseForm = () => {
  resetShippingAddress();

  closeForm();
};

const handleEWBNumberValidation = async (number) => {
  return await validateEWBNumber({
    number,
    _id: null,
  });
};

const { isRightDrawerOpen } = storeToRefs(useAppStore());

const { invoice } = storeToRefs(useInvoiceStore());

watchEffect(() => {
  if (unref(ewayBillRequestFormState).view && unref(isRightDrawerOpen)) {
    temp_shipping_address.value = unref(formData)?.shipping_address;

    unref(formData).bill_id = unref(invoice)?.bill?.number;
  }
});

const heading = computed(() => {
  let tempHeading = " E-Way Bill";

  if (unref(ewayBillRequestFormState).view) {
    return "View" + tempHeading;
  }

  if (unref(ewayBillRequestFormState).edit) {
    return "Edit" + tempHeading;
  }

  if (unref(isRequestAgainstPurchaseBill)) {
    return "Add" + tempHeading;
  }

  return "Request" + tempHeading;
});

const secondaryCta = computed(() => {
  if (unref(ewayBillRequestFormState).view) {
    return {};
  }

  return { visible: true };
});
</script>
